import { format, parse } from 'date-fns';
import { CalendarIcon, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '../lib/utils';
import { Button } from '../primitives/button';
import { Calendar } from '../primitives/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../primitives/popover';
import {
  CALENDAR_COMPONENTS,
  CALENDAR_FORMATTERS,
  CALENDAR_SINGLE_CLASS_NAMES,
} from './calendar-theme';

const ISO_DATE = 'yyyy-MM-dd';

export interface DatePickerProps {
  /** ISO date string `YYYY-MM-DD` (or empty for unset). */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Show a clear button when a date is selected. Pointer-only, like Picker's
   * and Combobox's; from the keyboard, Delete or Backspace on the field clears.
   */
  clearable?: boolean;
  /**
   * Sizes the field (`w-40`, `h-9 w-44`, …). Applied to the box that holds the
   * trigger and the clear button, so the clear button tracks the trigger's edge.
   * To style the trigger button itself, use `triggerClassName`.
   */
  className?: string;
  /** Classes for the trigger button (height, text, border, …). */
  triggerClassName?: string;
  id?: string;
  /** Visual indicator for invalid input (passed through aria-invalid). */
  ariaInvalid?: boolean;
  /**
   * This picker is an "as of" / time-travel control: the panel around it shows
   * the world as it stood on the chosen day rather than as it stands now.
   *
   * Set it and the control goes amber — but only once a date is actually
   * picked, because an empty as-of means "today" and today is not time travel.
   * Amber is reserved for exactly this state (see `--as-of-*` in the app's
   * `globals.css`); nothing else in this component may use it.
   */
  asOf?: boolean;
}

/**
 * Date picker: button + popover + calendar. Stores value as `YYYY-MM-DD` to
 * match the API and the underlying DATE column.
 *
 * The panel is a floating white card at the 12px popup radius rather than a
 * menu at 6px. Its caption is two jump controls (see `CaptionJump`) rather than
 * a static label, which is the whole of the navigation beyond the two chevrons.
 */
export function DatePicker({
  value,
  onChange,
  placeholder = 'Pick a date',
  disabled,
  clearable = false,
  className,
  triggerClassName,
  id,
  ariaInvalid,
  asOf = false,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Set by a clear click; consumed once the clear button has unmounted.
  const clearedRef = useRef(false);
  const selected = value ? parse(value, ISO_DATE, new Date()) : undefined;

  // The clear control is a SIBLING of the trigger, laid over its right edge.
  // Nested inside the trigger it was invalid HTML (<button> in <button>),
  // unreachable for assistive tech, and its click toggled the popover.
  const showClear = clearable && !!value;

  // The clear button unmounts with the value, dropping focus on <body>; hand it
  // back to the field. Only then: if the parent ignored the clear, the button
  // is still there and keeps focus.
  useEffect(() => {
    if (!showClear && clearedRef.current && document.activeElement === document.body) {
      triggerRef.current?.focus();
    }
    clearedRef.current = false;
  }, [showClear]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={cn('relative inline-flex w-full', className)}>
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            id={id}
            type="button"
            variant="outline"
            disabled={disabled}
            aria-invalid={ariaInvalid}
            aria-keyshortcuts={showClear ? 'Delete Backspace' : undefined}
            onKeyDown={(e) => {
              if (showClear && (e.key === 'Delete' || e.key === 'Backspace')) {
                e.preventDefault();
                onChange('');
              }
            }}
            className={cn(
              'w-full justify-start text-left font-normal',
              !value && 'text-muted-foreground',
              triggerClassName,
              // Pinned last, so neither the size variant nor `triggerClassName`
              // can move the slot the clear button's `right-3` lines up with.
              // Hovering the clear button keeps the whole field highlighted, as
              // it was when the button sat inside the trigger.
              showClear &&
                'pr-3 has-[>svg]:pr-3 has-[+button:hover]:bg-accent has-[+button:hover]:text-accent-foreground dark:has-[+button:hover]:bg-input/50',
            )}
          >
            <CalendarIcon className="size-4" />
            <span className="flex-1">{selected ? format(selected, 'PP') : placeholder}</span>
            {/* Reserves the clear button's slot so the label never runs under it. */}
            {showClear ? <span aria-hidden className="ml-1 size-5 shrink-0" /> : null}
          </Button>
        </PopoverTrigger>
        {showClear ? (
          <button
            type="button"
            aria-label="Clear date"
            tabIndex={-1}
            disabled={disabled}
            className="absolute top-1/2 right-3 inline-flex size-5 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
            onClick={() => {
              clearedRef.current = true;
              onChange('');
            }}
          >
            <X className="size-3" />
          </button>
        ) : null}
      </div>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(date) => {
            if (date) {
              onChange(format(date, ISO_DATE));
              setOpen(false);
            }
          }}
          autoFocus
          navLayout="around"
          showOutsideDays={false}
          components={CALENDAR_COMPONENTS}
          formatters={CALENDAR_FORMATTERS}
          classNames={CALENDAR_SINGLE_CLASS_NAMES}
        />
      </PopoverContent>
    </Popover>
  );
}
